import React, { useState, useEffect, useCallback, useRef } from 'react';

import { Table, TableBody, TableCell, TableContainer, TablePagination, TableHead, TableRow, Paper, Button, IconButton, Box, CircularProgress, TextField, Tabs, Tab, Tooltip } from '@mui/material';
import { getIcon } from '../../utils/IconHandler';
import { useTranslation } from 'react-i18next';
import { getReplenishment, getReplenishmentById } from '../../services/ReplenishmentService';
import { permissionOptions } from '../../utils/Enums';
import { paymentStatusOptions } from '../../utils/Enums';
import { formatDate } from '../../utils/DateUtils';
import ViewReplenishmentDetailsModal from './ViewReplenishmentDetailsModal';
import EditReplenishmentModal from './EditReplenishmentModal';
import AddReplenishmentModal from './AddReplenishmentModal';
import TableRefreshButton from '../TableRefreshButton/TableRefreshButton';

import { formatMoney } from '../../utils/currency';
const getReplenishmentCategoryValue = (category) => {
  if (typeof category === 'number') return category;
  if (typeof category === 'string') return category === 'RETAIL_PRODUCTS' ? 2 : 1;
  if (category?.value) return category.value;
  return 1;
};

const StockReplenishmentsTable = ( { locationId, replenishmentCategory = null } ) => {
  
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [replenishments, setReplenishments] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItemsModalOpen, setEditItemsModalOpen] = useState(false);
  const [selectedReplenishment, setSelectedReplenishment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [receptionStatus, setReceptionStatus] = useState(1);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Ref para manejar debounce
  const searchTimeoutRef = useRef(null);

  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionId) => userPermissions.includes(permissionId);
  const replenishmentPermission = replenishmentCategory === 2
    ? 'REPLENISHMENT_RETAIL_PRODUCTS'
    : 'REPLENISHMENT_SUPPLIES';
  const canManageReplenishment = hasPermission(
    permissionOptions.find((p) => p.label === replenishmentPermission).id
  );

  const loadReplenishmentDetail = async (replenishment) => {
    setDetailLoadingId(replenishment.id);
    try {
      return await getReplenishmentById(replenishment.id);
    } catch (error) {
      console.error('Error fetching replenishment detail:', error);
      return replenishment;
    } finally {
      setDetailLoadingId(null);
    }
  };

  const onView = async (replenishment) => {
    const detail = await loadReplenishmentDetail(replenishment);
    setSelectedReplenishment(detail);
    setViewModalOpen(true);
  };
  
  const onEdit = async (replenishment) => {
    const detail = await loadReplenishmentDetail(replenishment);
    setSelectedReplenishment(detail);
    setEditModalOpen(true);
  };

  const onEditItems = async (replenishment) => {
    const detail = await loadReplenishmentDetail(replenishment);
    setSelectedReplenishment(detail);
    setEditItemsModalOpen(true);
  };
  
  const handleCloseViewModal = () => {
    setViewModalOpen(false);
  };
  
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
  };

  const handleCloseEditItemsModal = () => {
    setEditItemsModalOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchReplenishment(newPage + 1, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchReplenishment(1, newRowsPerPage, searchTerm);
  };

  const fetchReplenishment = useCallback(async (pageNum = 1, pageSize = 10, search = '') => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const data = await getReplenishment(locationId, pageNum, pageSize, cleanSearch, replenishmentCategory, receptionStatus);

      if (data && typeof data === 'object') {
        setReplenishments(data.replenishments || data.data || []);
        setTotalRows(data.total || data.totalCount || 0);
      } else {
        setReplenishments(data || []);
        setTotalRows(data?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching replenishment:', error);
      setReplenishments([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [locationId, replenishmentCategory, receptionStatus]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchReplenishment(page + 1, rowsPerPage, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchReplenishment]);

  useEffect(() => {
    getReplenishment(locationId, 1, 1, '', replenishmentCategory, 2)
      .then((data) => setPendingCount(data?.total || data?.totalCount || 0))
      .catch(() => setPendingCount(0));
  }, [locationId, replenishmentCategory, receptionStatus]);

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (searchTerm) => {
    setSearchTerm(searchTerm);
    setPage(0);
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchReplenishment(1, rowsPerPage, searchTerm);
    }, 500);
  };

  const handleAddClick = () => {
    setAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setAddModalOpen(false);
  };

  const onSave = (newReplenishment) => {
    // Refetch data after adding
    fetchReplenishment(page + 1, rowsPerPage, searchTerm);
  };

  const getPaymentStatusColor = (paymentStatusId) => {
    const status = paymentStatusOptions.find(status => status.id === paymentStatusId);
    return status ? { label: t(status.label), color: status.color } : { label: t('unknown_status'), color: 'grey' };
  };

  const handleSaveEdit = async (updatedReplenishment) => {
    // Si veníamos de "Pendientes", al cambiar de pestaña el efecto recarga solo.
    // Si ya estamos en "Confirmadas" no cambia ningún estado, así que hay que recargar a mano.
    if (updatedReplenishment?.receptionStatus === 1 && receptionStatus !== 1) {
      setPage(0);
      setReceptionStatus(1);
      return;
    }

    fetchReplenishment(page + 1, rowsPerPage, searchTerm);
  };

  const handleConfirmReception = async (replenishment) => {
    const detail = await loadReplenishmentDetail(replenishment);
    setSelectedReplenishment(detail);
    setReceiveModalOpen(true);
  };

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Tabs value={receptionStatus} onChange={(_, value) => { setReceptionStatus(value); setPage(0); }} sx={{ mb: 2 }}>
          <Tab value={1} label="Confirmadas" />
          <Tab value={2} label={pendingCount > 0 ? `Pendientes (${pendingCount})` : 'Pendientes'} />
        </Tabs>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <TextField
            label={t('search')}
            placeholder={t('search_by_supplier_invoice')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ width: '300px' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableRefreshButton
              loading={loading}
              onClick={() => fetchReplenishment(page + 1, rowsPerPage, searchTerm)}
            />
            {canManageReplenishment && receptionStatus !== 2 && (
              <Button variant="outlined" startIcon={getIcon('add')} onClick={() => handleAddClick()}>
                Nueva reposición
              </Button>
            )}
          </Box>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 900 }}>
            <TableContainer component={Paper} style={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="left"><span className="roboto-regular">{t('date')}</span></TableCell>
                    <TableCell align="center"><span className="roboto-regular">{t('supplier')}</span></TableCell>
                    <TableCell align="center"><span className="roboto-regular">{t('payment_status')}</span></TableCell>
                    <TableCell align="center"><span className="roboto-regular">{t('invoice')}</span></TableCell>
                    <TableCell align="center"><span className="roboto-regular">{t('total_price')}</span></TableCell>
                    <TableCell align="right" sx={{ minWidth: canManageReplenishment ? 240 : 64 }}><span className="roboto-regular">{t('actions')}</span></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box py={3} display="flex" justifyContent="center">
                          <CircularProgress size={32} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : replenishments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                        {receptionStatus === 2
                          ? 'No tenés pedidos pendientes. Se generan solos cuando llega una factura de un proveedor con ítems vinculados.'
                          : 'Todavía no hay reposiciones confirmadas.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    replenishments.map((replenishment) => {
                      const paymentStatus = getPaymentStatusColor(replenishment.paymentStatus);
                      const supplierName =
                        replenishment.supplierDisplayName ||
                        replenishment.supplier?.businessName ||
                        replenishment.supplier?.name ||
                        t('no_supplier');
                      return (
                        <TableRow key={replenishment.id}>
                          <TableCell><span className="roboto-light">{formatDate(replenishment.date)}</span></TableCell>
                          <TableCell align="center"><span className="roboto-light">{supplierName}</span></TableCell>
                          <TableCell align="center" style={{ color: paymentStatus.color }}>
                            <span className="roboto-light">{paymentStatus.label}</span>
                          </TableCell>
                          <TableCell align="center"><span className="roboto-light">{replenishment.invoice}</span></TableCell>
                          <TableCell align="center"><span className="roboto-light">{formatMoney(replenishment.totalPrice, replenishment.currency)}</span></TableCell>                    
                          <TableCell align="right" sx={{ minWidth: canManageReplenishment ? 240 : 64, whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, flexWrap: 'nowrap' }}>
                              <Tooltip title="Ver detalle">
                                <IconButton
                                  aria-label="Ver detalle"
                                  disabled={detailLoadingId === replenishment.id}
                                  onClick={() => onView(replenishment)}
                                >
                                  {getIcon('eye')}
                                </IconButton>
                              </Tooltip>
                              {canManageReplenishment && replenishment.receptionStatus !== 2 && (
                                <Tooltip title="Registrar pago">
                                  <IconButton
                                    aria-label="Registrar pago"
                                    disabled={detailLoadingId === replenishment.id}
                                    onClick={() => onEdit(replenishment)}
                                  >
                                    {getIcon('money')}
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canManageReplenishment && (
                                <Tooltip title={t('edit_replenishment_items') || 'Editar reposición'}>
                                  <IconButton
                                    aria-label={t('edit_replenishment_items') || 'Editar reposición'}
                                  disabled={detailLoadingId === replenishment.id}
                                  onClick={() => onEditItems(replenishment)}
                                >
                                    {getIcon('settings')}
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canManageReplenishment && replenishment.receptionStatus === 2 && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={getIcon('checkmarkDone')}
                                  disabled={detailLoadingId === replenishment.id}
                                  onClick={() => handleConfirmReception(replenishment)}
                                  sx={{ minWidth: 130, whiteSpace: 'nowrap' }}
                                >
                                  Confirmar
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalRows}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Box>
      </Paper>

      <AddReplenishmentModal
        locationId={locationId}
        open={addModalOpen}
        onClose={handleCloseModal}
        onSave={onSave}
        defaultReplenishmentCategory={replenishmentCategory}
        initialReceptionStatus={receptionStatus}
      />

      <ViewReplenishmentDetailsModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        replenishment={selectedReplenishment}
      />

      <AddReplenishmentModal
        locationId={locationId}
        open={receiveModalOpen}
        onClose={() => setReceiveModalOpen(false)}
        onSave={handleSaveEdit}
        defaultReplenishmentCategory={selectedReplenishment ? getReplenishmentCategoryValue(selectedReplenishment.replenishmentCategory) : replenishmentCategory}
        mode="receive"
        replenishment={selectedReplenishment}
      />

      <EditReplenishmentModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        replenishment={selectedReplenishment}
        onSave={handleSaveEdit}
      />

      <AddReplenishmentModal
        locationId={locationId}
        open={editItemsModalOpen}
        onClose={handleCloseEditItemsModal}
        onSave={handleSaveEdit}
        defaultReplenishmentCategory={selectedReplenishment ? getReplenishmentCategoryValue(selectedReplenishment.replenishmentCategory) : replenishmentCategory}
        mode="edit"
        replenishment={selectedReplenishment}
      />
    </>
  );
};

export default StockReplenishmentsTable;
